import { Form } from '@data-delivery/d3s';
import {
  useBusinessGroupAssignments,
  useBusinessGroupsRQ,
  useContentAccessRolesQuery,
  useCurrentUserRoles,
  useLandingFolderRestrictionsByType,
  useSftpUserByIdQuery,
} from '@data-delivery/permission-ui-data-access-state';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { isOperator } from '../../utils';
import { isAdminRolePresent } from '../../utils/roles';
import { useUserForm } from './hooks/use-user-form';
import { useUserMutation } from './hooks/use-user-mutation';
import { UserFoldersSection } from './sections/user-folders-section';
import { UserIdentitySection } from './sections/user-identity-section';
import { UserListsSection } from './sections/user-lists-section';
import { UserRolesSection } from './sections/user-roles-section';

export type UserEditorMode = 'create' | 'edit';

interface UserEditorProps {
  mode: UserEditorMode;
  user?: {
    id: string;
    loginId: string;
    distributionList?: string[];
    ipWhitelist?: string[];
    landingFolder?: string;
    sfAccountId?: string;
    contentAccessRole?: string;
    orgName?: string;
    rootFolder?: string;
    serviceAccountOwner?: string;
    lastModified?: number;
    assignments?: string[];
    firstName?: string;
    lastName?: string;
    isSystemUser?: boolean;
    isLoginAdmin?: boolean;
    isEnabled?: boolean;
  };
  onSuccess?: () => void;
  onCancel?: () => void;
  onSubmittingChange?: (submitting: boolean) => void;
  businessGroupReadOnly?: boolean;
  initialBusinessGroupId?: string;
}

function LoadingSkeleton() {
  return (
    <div className='space-y-4 animate-pulse' data-state='loading'>
      <div className='h-6 bg-muted rounded' />
      <div className='h-10 bg-muted rounded' />
      <div className='h-10 bg-muted rounded' />
      <div className='h-24 bg-muted rounded' />
      <div className='h-10 bg-muted rounded' />
      <div className='h-10 bg-muted rounded' />
      <div className='h-10 bg-muted rounded' />
    </div>
  );
}

function ErrorMessage() {
  return (
    <div className='text-sm text-red-600'>
      Failed to load user details. You may still edit existing local values.
    </div>
  );
}

export function UserEditor({
  mode,
  user,
  onSuccess,
  onSubmittingChange,
  businessGroupReadOnly = true,
  initialBusinessGroupId,
}: UserEditorProps) {
  const { businessGroupId: routeBusinessGroupId, userId: routeUserId } = useParams();
  const { data: businessGroupsData } = useBusinessGroupsRQ();

  const selectedBusinessGroupId =
    initialBusinessGroupId || routeBusinessGroupId || businessGroupsData?.[0]?.id;

  const { assignments: bgAssignments } = useBusinessGroupAssignments(selectedBusinessGroupId);
  const { data: restrictionsData } = useLandingFolderRestrictionsByType();
  const { data: contentAccessRolesData } = useContentAccessRolesQuery();

  const restrictedNames = useMemo(
    () => new Set<string>((restrictionsData ?? []).map(r => r.name)),
    [restrictionsData],
  );

  const restrictedFolders = useMemo(
    () => new Set<string>((restrictionsData ?? []).map(r => r.folder).filter(Boolean)),
    [restrictionsData],
  );

  const businessGroupOptions = useMemo(
    () =>
      (businessGroupsData || []).map(bg => ({
        value: bg.id,
        label: (bg as { shortName?: string; name?: string }).shortName || bg.name || bg.id,
      })),
    [businessGroupsData],
  );

  const assignmentOptions = useMemo(
    () => bgAssignments.map(a => ({ value: a.id, label: (a as { name?: string }).name || a.id })),
    [bgAssignments],
  );

  const contentAccessRoleOptions = useMemo(
    () => (contentAccessRolesData ?? []).map(r => ({ value: r.id, label: r.name || r.id })),
    [contentAccessRolesData],
  );

  const userId = mode === 'edit' ? routeUserId || user?.id : undefined;
  const userByIdQuery = useSftpUserByIdQuery(userId);
  const { data: currentUserRoles = [] } = useCurrentUserRoles();
  const canEditLoginAsAdmin = isAdminRolePresent(currentUserRoles);
  const canEditLandingFolder = isOperator(currentUserRoles);
  const form = useUserForm({
    mode,
    user: userByIdQuery.data || user,
    restrictedNames,
    restrictedFolders,
    requireAssignments: businessGroupReadOnly,
    currentUserRoles,
  });

  const { submit, isPending } = useUserMutation({
    mode,
    userId: user?.id,
    lastModified: user?.lastModified,
    businessGroupId: selectedBusinessGroupId,
    requireAssignments: businessGroupReadOnly,
    onSuccess,
  });

  const isSystemUser = form.watch('isSystemUser');

  useEffect(() => {
    onSubmittingChange?.(isPending);
  }, [isPending, onSubmittingChange]);

  const isLoading = mode === 'edit' && userId && userByIdQuery.isLoading;
  const hasError = mode === 'edit' && userId && userByIdQuery.isError;

  return (
    <Form {...form}>
      <form
        id='user-editor-form'
        className='flex flex-col gap-6 p-4'
        onSubmit={form.handleSubmit(submit)}
        data-mode={mode}
      >
        {isLoading && <LoadingSkeleton />}

        {!isLoading && (
          <>
            <UserIdentitySection control={form.control} mode={mode} isSystemUser={isSystemUser} />
            <UserFoldersSection
              control={form.control}
              canEditLandingFolder={canEditLandingFolder}
            />
            <UserRolesSection
              control={form.control}
              showBusinessGroup={businessGroupReadOnly}
              showAssignments={businessGroupReadOnly}
              canEditLoginAsAdmin={canEditLoginAsAdmin}
              selectedBusinessGroupId={selectedBusinessGroupId}
              businessGroupOptions={businessGroupOptions}
              contentAccessRoleOptions={contentAccessRoleOptions}
              assignmentOptions={assignmentOptions}
            />
            <UserListsSection control={form.control} />
          </>
        )}

        {hasError && <ErrorMessage />}
      </form>
    </Form>
  );
}

export default UserEditor;
